import React from 'react';
import { useHybridTheme } from '../themes/useHybridTheme';
import { X } from 'lucide-react';
import { Prompt, Folder } from '../types';
import { motion, AnimatePresence } from 'framer-motion';
import { Z_LAYERS } from '../constants/zLayers';
import Button from './Button';
import { BackupManager } from '../utils/backupManager';

interface PromptFormProps {
  folders: Folder[];
  existingTags: string[];
  onClose: () => void;
  onSave: (prompt: Prompt) => void;
  initialPrompt?: Prompt;
  onAddFolder?: (folderName: string) => Promise<string>;
  currentFolderId?: string | null;
}

export default function PromptForm({
  folders,
  existingTags,
  onClose,
  onSave,
  initialPrompt,
  onAddFolder,
  currentFolderId,
}: PromptFormProps) {
  const [title, setTitle] = React.useState(initialPrompt?.title || '');
  const [description, setDescription] = React.useState(initialPrompt?.description || '');
  const [text, setText] = React.useState(initialPrompt?.text || '');
  const [folder, setFolder] = React.useState(() => {
    if (initialPrompt?.folder) return initialPrompt.folder;
    if (currentFolderId && currentFolderId !== 'all' && currentFolderId !== 'unsorted') {
      return currentFolderId;
    }
    return folders[0]?.id || '';
  });
  const [tags, setTags] = React.useState<string[]>(initialPrompt?.tags || []);
  const [usageCount, setUsageCount] = React.useState(initialPrompt?.usageCount || 0);

  // Update usageCount when initialPrompt changes
  React.useEffect(() => {
    if (initialPrompt?.usageCount !== undefined) {
      setUsageCount(initialPrompt.usageCount);
    }
  }, [initialPrompt?.usageCount]);

  // The folder default is seeded once at mount. If folders load async after
  // mount (or the sidebar selection resolves later), re-derive it — otherwise
  // the select stays on "" and ignores the currently selected folder. Only
  // fills an empty selection so it never clobbers a user's manual choice.
  React.useEffect(() => {
    if (folder) return;
    if (initialPrompt?.folder) {
      setFolder(initialPrompt.folder);
    } else if (currentFolderId && currentFolderId !== 'all' && currentFolderId !== 'unsorted') {
      setFolder(currentFolderId);
    } else if (folders[0]?.id) {
      setFolder(folders[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt?.folder, currentFolderId, folders]);
  const [newFolderInput, setNewFolderInput] = React.useState('');
  const [tagInput, setTagInput] = React.useState('');
  const [showNewFolder, setShowNewFolder] = React.useState(false);
  const [showVariableInput, setShowVariableInput] = React.useState(false);
  const [variableNameInput, setVariableNameInput] = React.useState('');
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
  const { getClasses, getColor } = useHybridTheme();

  const [showFolderWarning, setShowFolderWarning] = React.useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Check if there's unsaved folder input
    if (showNewFolder && newFolderInput.trim()) {
      setShowFolderWarning(true);
      return;
    }

    // Add diagnostic log to help identify the issue
    console.log('PromptForm: Submitting form with values:', {
      title,
      description,
      text,
      folder,
      tags,
      usageCount,
    });

    onSave({
      id: initialPrompt?.id || crypto.randomUUID(),
      title,
      description,
      text,
      folder,
      tags,
      lastChanged: Date.now(),
      usageCount: usageCount,
    });

    // Check if backup is needed after saving a prompt
    void BackupManager.checkAndCreateBackup();

    onClose();
  };

  const handleTagInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (newTag && !tags.includes(newTag)) {
        setTags([...tags, newTag]);
      }
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const insertVariable = () => {
    const name = variableNameInput.trim();
    if (!name) return;
    const placeholder = `{{${name}}}`;
    const textarea = textAreaRef.current;
    // The textarea keeps its last selection even after focus moves to the
    // variable input, so insert at the cursor rather than appending.
    const start = textarea?.selectionStart ?? text.length;
    const end = textarea?.selectionEnd ?? text.length;
    setText(text.slice(0, start) + placeholder + text.slice(end));
    setVariableNameInput('');
    setShowVariableInput(false);
    requestAnimationFrame(() => {
      if (!textarea) return;
      textarea.focus();
      const cursor = start + placeholder.length;
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={getClasses('fixed inset-0 flex items-center justify-center')}
      style={{
        backgroundColor: getColor('form-modalOverlay'),
        zIndex: Z_LAYERS.MODAL,
      }}
    >
      {/* Folder Warning Dialog */}
      <AnimatePresence>
        {showFolderWarning && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={getClasses('fixed inset-0 flex items-center justify-center')}
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: Z_LAYERS.NESTED_MODAL }}
          >
            <motion.div
              className={getClasses('rounded-lg shadow-xl p-6 max-w-md')}
              style={{ backgroundColor: getColor('form-cardBackground') }}
            >
              <h3
                className={getClasses('text-xl font-semibold mb-4')}
                style={{ color: getColor('form-inputText') }}
              >
                Unsaved Folder
              </h3>
              <p className={getClasses('mb-6')} style={{ color: getColor('form-inputText') }}>
                You have entered a new folder name but haven't saved it. Click 'Add' first before
                submitting.
              </p>
              <div className="flex justify-end">
                <Button
                  onClick={() => {
                    setShowFolderWarning(false);
                  }}
                  variant="primary"
                  size="md"
                  type="button"
                >
                  OK
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className={getClasses('rounded-lg shadow-xl w-[800px] h-[580px] flex flex-col')}
        style={{
          backgroundColor: getColor('form-cardBackground'),
        }}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <motion.h2
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              className={getClasses('text-2xl font-semibold')}
              style={{
                color: getColor('form-inputText'),
              }}
            >
              {initialPrompt ? 'Edit Prompt' : 'New Prompt'}
            </motion.h2>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className={getClasses('hover:opacity-80')}
              style={{
                color: getColor('form-inputText'),
              }}
              aria-label="Close prompt form"
            >
              <X className="w-6 h-6" />
            </motion.button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col h-[450px]">
            <div className="grid grid-cols-2 gap-6 flex-1">
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="prompt-title"
                    className={getClasses('block text-sm font-medium mb-1')}
                    style={{ color: getColor('form-inputText') }}
                  >
                    Title
                  </label>
                  <motion.input
                    id="prompt-title"
                    whileFocus={{ scale: 1.01 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    type="text"
                    value={title}
                    placeholder="e.g. Weekly status report email"
                    onChange={e => setTitle(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === '/') {
                        e.stopPropagation();
                      }
                    }}
                    className={getClasses(
                      'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2'
                    )}
                    style={{
                      backgroundColor: getColor('form-inputBackground'),
                      borderColor: getColor('form-inputBorder'),
                      color: getColor('form-inputText'),
                    }}
                    required
                    aria-label="Prompt title"
                  />
                </div>

                <div>
                  <label
                    htmlFor="prompt-description"
                    className={getClasses('block text-sm font-medium mb-1')}
                    style={{ color: getColor('form-inputText') }}
                  >
                    Description
                  </label>
                  <p
                    id="prompt-description-help"
                    className={getClasses('text-xs mb-1')}
                    style={{ color: getColor('form-inputText'), opacity: 0.7 }}
                  >
                    Shown in your prompt list so you can find this prompt later (max 200
                    characters).
                  </p>
                  <motion.textarea
                    id="prompt-description"
                    aria-describedby="prompt-description-help"
                    whileFocus={{ scale: 1.01 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    value={description}
                    onChange={e => setDescription(e.target.value.slice(0, 200))}
                    onKeyDown={e => {
                      if (e.key === '/') {
                        e.stopPropagation();
                      }
                    }}
                    className={getClasses(
                      'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2'
                    )}
                    style={{
                      backgroundColor: getColor('form-inputBackground'),
                      borderColor: getColor('form-inputBorder'),
                      color: getColor('form-inputText'),
                    }}
                    rows={2}
                    maxLength={200}
                    aria-label="Prompt description"
                  />
                </div>

                {/* Folders */}
                <div>
                  <label
                    className={getClasses('block text-sm font-medium mb-1')}
                    style={{ color: getColor('form-inputText') }}
                  >
                    Folder
                  </label>
                  <AnimatePresence mode="wait">
                    {showNewFolder ? (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="flex gap-2"
                      >
                        <motion.input
                          id="new-folder-input"
                          whileFocus={{ scale: 1.01 }}
                          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                          type="text"
                          value={newFolderInput}
                          onChange={e => setNewFolderInput(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === '/') {
                              e.stopPropagation();
                            }
                          }}
                          className={getClasses(
                            'flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2'
                          )}
                          style={{
                            backgroundColor: getColor('form-inputBackground'),
                            borderColor: getColor('form-inputBorder'),
                            color: getColor('form-inputText'),
                          }}
                          placeholder="New folder name"
                          aria-label="New folder name"
                        />
                        <Button
                          onClick={async () => {
                            if (newFolderInput) {
                              console.log(
                                'PromptForm: Adding new folder with name:',
                                newFolderInput
                              );
                              const newFolderId = await onAddFolder?.(newFolderInput);
                              console.log('PromptForm: Received new folder ID:', newFolderId);
                              if (newFolderId) {
                                setFolder(newFolderId);
                              }
                              setShowNewFolder(false);
                              setNewFolderInput('');
                            }
                          }}
                          variant="primary"
                          size="md"
                          type="button"
                        >
                          Add
                        </Button>
                      </motion.div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="flex gap-2"
                      >
                        <select
                          value={folder}
                          onChange={e => setFolder(e.target.value)}
                          className={getClasses(
                            'flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2'
                          )}
                          style={{
                            backgroundColor: getColor('form-inputBackground'),
                            borderColor: getColor('form-inputBorder'),
                            color: getColor('form-inputText'),
                          }}
                        >
                          <option value="">Select a folder</option>
                          {folders.map(f => (
                            <option key={f.id} value={f.id}>
                              {f.name}
                            </option>
                          ))}
                        </select>
                        <Button
                          onClick={() => setShowNewFolder(true)}
                          variant="secondary"
                          size="md"
                          type="button"
                        >
                          New Folder
                        </Button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div>
                  <label
                    className={getClasses('block text-sm font-medium mb-1')}
                    style={{ color: getColor('form-inputText') }}
                  >
                    Tags
                  </label>
                  <p
                    id="tag-input-help"
                    className={getClasses('text-xs mb-1')}
                    style={{ color: getColor('form-inputText'), opacity: 0.7 }}
                  >
                    Add keywords for filtering and search.
                  </p>
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2 mb-2">
                      <AnimatePresence>
                        {tags.map(tag => (
                          <motion.span
                            key={tag}
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ type: 'spring', damping: 20 }}
                            className={getClasses(
                              'inline-flex items-center gap-1 px-2 py-1 rounded-md'
                            )}
                            style={{
                              backgroundColor: getColor('form-buttonSecondary'),
                              color: getColor('form-inputText'),
                            }}
                          >
                            {tag}
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              type="button"
                              onClick={() => removeTag(tag)}
                              className={getClasses('hover:opacity-80')}
                              style={{
                                color: getColor('form-inputText'),
                              }}
                              aria-label={`Remove tag ${tag}`}
                            >
                              <X className="w-3 h-3" />
                            </motion.button>
                          </motion.span>
                        ))}
                      </AnimatePresence>
                    </div>
                    <div className="flex gap-2">
                      <motion.input
                        id="tag-input"
                        aria-describedby="tag-input-help"
                        whileFocus={{ scale: 1.01 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                        type="text"
                        value={tagInput}
                        onChange={e => setTagInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                          }
                          if (e.key === '/') {
                            e.stopPropagation();
                          }
                          handleTagInput(e);
                        }}
                        onBlur={() => {
                          if (tagInput.trim() && !tags.includes(tagInput.trim())) {
                            setTags([...tags, tagInput.trim()]);
                            setTagInput('');
                          }
                        }}
                        className={getClasses(
                          'flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2'
                        )}
                        style={{
                          backgroundColor: getColor('form-inputBackground'),
                          borderColor: getColor('form-inputBorder'),
                          color: getColor('form-inputText'),
                        }}
                        placeholder="Enter tags (press Enter or comma to add)"
                        aria-label="Enter tags (press Enter or comma to add)"
                      />
                      {existingTags.length > 0 && (
                        <select
                          onChange={e => {
                            if (e.target.value && !tags.includes(e.target.value)) {
                              setTags([...tags, e.target.value]);
                            }
                            e.target.value = '';
                          }}
                          className={getClasses(
                            'px-3 py-2 border rounded-md focus:outline-none focus:ring-2'
                          )}
                          style={{
                            backgroundColor: getColor('form-inputBackground'),
                            borderColor: getColor('form-inputBorder'),
                            color: getColor('form-inputText'),
                          }}
                        >
                          <option value="">Existing tags</option>
                          {existingTags
                            .filter(tag => !tags.includes(tag))
                            .sort((a, b) => a.localeCompare(b))
                            .map(tag => (
                              <option key={tag} value={tag}>
                                {tag}
                              </option>
                            ))}
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label
                        htmlFor="prompt-text"
                        className={getClasses('block text-sm font-medium')}
                        style={{ color: getColor('form-inputText') }}
                      >
                        Prompt text
                      </label>
                      <Button
                        onClick={() => setShowVariableInput(true)}
                        variant="secondary"
                        size="sm"
                        type="button"
                        aria-label="Insert a fill-in variable"
                      >
                        Insert Variable
                      </Button>
                    </div>
                    <p
                      id="prompt-text-help"
                      className={getClasses('text-xs mb-1')}
                      style={{ color: getColor('form-inputText'), opacity: 0.7 }}
                    >
                      The prompt that gets copied or sent. Wrap parts in {'{{double braces}}'} to
                      create fill-in variables you&apos;ll be asked for each time you use it.
                    </p>
                    <AnimatePresence>
                      {showVariableInput && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="flex gap-2 mb-2"
                        >
                          <motion.input
                            id="variable-name-input"
                            whileFocus={{ scale: 1.01 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                            type="text"
                            value={variableNameInput}
                            onChange={e => setVariableNameInput(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                insertVariable();
                              }
                              if (e.key === 'Escape') {
                                setShowVariableInput(false);
                                setVariableNameInput('');
                              }
                              if (e.key === '/') {
                                e.stopPropagation();
                              }
                            }}
                            className={getClasses(
                              'flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2'
                            )}
                            style={{
                              backgroundColor: getColor('form-inputBackground'),
                              borderColor: getColor('form-inputBorder'),
                              color: getColor('form-inputText'),
                            }}
                            placeholder="Variable name (e.g. project)"
                            aria-label="Variable name"
                            autoFocus
                          />
                          <Button
                            onClick={insertVariable}
                            variant="primary"
                            size="md"
                            type="button"
                          >
                            Insert
                          </Button>
                          <Button
                            onClick={() => {
                              setShowVariableInput(false);
                              setVariableNameInput('');
                            }}
                            variant="secondary"
                            size="md"
                            type="button"
                          >
                            Cancel
                          </Button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <motion.textarea
                      ref={textAreaRef}
                      id="prompt-text"
                      aria-describedby="prompt-text-help"
                      whileFocus={{ scale: 1.01 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                      value={text}
                      placeholder={
                        'e.g. Write a friendly status update about {{project}} for {{audience}}.'
                      }
                      onChange={e => setText(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === '/') {
                          e.stopPropagation();
                        }
                      }}
                      className={getClasses(
                        'w-full h-[300px] px-3 py-2 border rounded-md focus:outline-none focus:ring-2 resize-y'
                      )}
                      style={{
                        backgroundColor: getColor('form-inputBackground'),
                        borderColor: getColor('form-inputBorder'),
                        color: getColor('form-inputText'),
                      }}
                      required
                      aria-label="Prompt text"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mt-6 pt-6 pb-8 border-t">
              {/* Left side - Usage Count */}
              {initialPrompt?.usageCount !== undefined && (
                <div>
                  <label
                    className={getClasses('block text-sm font-medium mb-1')}
                    style={{ color: getColor('form-inputText') }}
                  >
                    Usage Count
                  </label>
                  <div
                    className={getClasses('px-3 py-2 border rounded-md')}
                    style={{
                      backgroundColor: getColor('form-inputBackground'),
                      borderColor: getColor('form-inputBorder'),
                      color: getColor('form-inputText'),
                      opacity: 0.8,
                    }}
                  >
                    {usageCount} {usageCount === 1 ? 'time' : 'times'}
                  </div>
                </div>
              )}

              {/* Right side - Action Buttons */}
              <div className="flex gap-3">
                <Button onClick={onClose} variant="secondary" size="md" type="button">
                  Cancel
                </Button>
                <Button variant="primary" size="md" type="submit">
                  {initialPrompt ? 'Save Changes' : 'Create Prompt'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
}
